import React, { useState, useEffect } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    Divider,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { LeadsFilters } from '../../../../hooks/Outreach/useLeads'
import { Campaign } from '../../../../models/Outreach/types'

interface Props {
    open: boolean
    onClose: () => void
    onApply: (filters: LeadsFilters) => void
    onClear: () => void
    currentFilters: LeadsFilters
    campaigns?: Campaign[]
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: '400px',
        },
    },
    dialogContent: {
        padding: theme.spacing(3),
    },
    formControl: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
}))

export const FilterDialog: React.FC<Props> = ({
    open,
    onClose,
    onApply,
    onClear,
    currentFilters,
    campaigns,
}) => {
    const classes = useStyles()
    const [localFilters, setLocalFilters] = useState<LeadsFilters>(
        currentFilters,
    )

    // Sync local filters when dialog opens or currentFilters change
    useEffect(() => {
        if (open) {
            setLocalFilters(currentFilters)
        }
    }, [open, currentFilters])

    const handleFilterChange = (key: keyof LeadsFilters, value: any) => {
        setLocalFilters((prev: LeadsFilters) => ({ ...prev, [key]: value }))
    }

    const handleApply = () => {
        onApply(localFilters)
        onClose()
    }

    const handleClear = () => {
        const clearedFilters: LeadsFilters = {
            page: 1,
            page_size: 25,
        }
        setLocalFilters(clearedFilters)
        onClear()
        onClose()
    }

    const hasActiveFilters = !!(
        localFilters.campaign_id ||
        localFilters.in_campaign !== undefined ||
        localFilters.has_email !== undefined ||
        localFilters.has_phone !== undefined
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Filter Options</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {/* Campaign Filter */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Campaign
                    </Typography>
                    <FormControl
                        className={classes.formControl}
                        variant="outlined"
                    >
                        <InputLabel>Campaign</InputLabel>
                        <Select
                            value={localFilters.campaign_id || ''}
                            onChange={(e) =>
                                handleFilterChange(
                                    'campaign_id',
                                    e.target.value || undefined,
                                )
                            }
                            label="Campaign"
                        >
                            <MenuItem value="">All Campaigns</MenuItem>
                            {campaigns?.map((campaign) => (
                                <MenuItem key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        className={classes.formControl}
                        variant="outlined"
                    >
                        <InputLabel>Campaign Status</InputLabel>
                        <Select
                            value={
                                localFilters.in_campaign === true
                                    ? 'in'
                                    : localFilters.in_campaign === false
                                    ? 'not_in'
                                    : ''
                            }
                            onChange={(e) => {
                                const value = e.target.value
                                handleFilterChange(
                                    'in_campaign',
                                    value === 'in'
                                        ? true
                                        : value === 'not_in'
                                        ? false
                                        : undefined,
                                )
                            }}
                            label="Campaign Status"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="in">In Campaign</MenuItem>
                            <MenuItem value="not_in">Not in Campaign</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Divider style={{ marginBottom: 16 }} />

                {/* Enrichment Filters */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Contact Information
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={localFilters.has_email === true}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'has_email',
                                        e.target.checked ? true : undefined,
                                    )
                                }
                            />
                        }
                        label="Has Email"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={localFilters.has_phone === true}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'has_phone',
                                        e.target.checked ? true : undefined,
                                    )
                                }
                            />
                        }
                        label="Has Phone"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                {hasActiveFilters && (
                    <Button onClick={handleClear} color="secondary">
                        Clear All
                    </Button>
                )}
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleApply}
                    color="primary"
                    variant="contained"
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    )
}
